import React, { useState } from 'react';
import Head from 'next/head';
import Image from 'next/image';
import Link from 'next/link';
import { ButtonComponent, SlashContainer } from '@zolteam/axenergie-ui-library';
import { useRouter } from 'next/router';
import { useLockBodyScroll, useMeasure, useToggle } from 'react-use';

// Constants
import strings from '../../../constants/Strings';
import colors from '../../../constants/Colors';

// Components
import Menu from '../../../components/Molecules/Menu';
import Footer from '../../../components/Molecules/Footer';
import ReassuranceBanner from '../../../components/Molecules/ReassuranceBanner';
import AgencyContactBlock from '../../../components/Atoms/AgencyContactBlock';
import GoodReasonsBlock from '../../../components/Atoms/GoodReasonsBlock';
import PrestationCard from '../../../components/Atoms/PrestationCard';
import SearchEngine from '../../../components/Organisms/SearchEngine';
import HeaderWithSlashContainer from '../../../components/Atoms/HeaderWithSlashContainer';

// Services and hooks
import useIsMobile from '../../../hooks/useIsMobile';
import AgencyService from '../../../services/api/AgencyService';
import menuAndFooterService from '../../../services/contentful/menuAndFooterService';
import PrestationService from '../../../services/api/PrestationService';

// Utils
import utils from '../../../utils/formatCMS';
import prestationUtils from '../../../utils/prestationUtils';
import headUtils from '../../../utils/headUtils';
import searchEngineUtils from '../../../utils/searchEngineUtils';
import urlUtils from '../../../utils/urlUtils';

// Images
import logo from '../../../public/Images/logo-white.webp';
import arrow from '../../../public/icons/arrow-down.svg';
import ContactModal from '../../../components/Organisms/ContactModal';
import BreakdownInfoCard from '../../../components/Atoms/BreakDownInfoCard';

const PrestationDetails = (props) => {
  const [isMobile,, screenWidth] = useIsMobile();
  const router = useRouter();
  const [contentBlockRef, { height: contentBlockHeight }] = useMeasure();
  const [contactBlockRef, { height: contactBlockHeight }] = useMeasure();

  // modal related variables
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [defaultModalSubject, setDefaultModalSubject] = useState(4);
  const [locked, toggleLocked] = useToggle(false);
  useLockBodyScroll(locked);

  const toggleContactModal = (value) => {
    setIsContactModalOpen(value);
    toggleLocked();
  };

  const getCardWidth = () => (screenWidth < 432 ? screenWidth - 32 : 400);

  const getDefaultSubject = (prestationType) => {
    switch (prestationType) {
      case 1:
        return 1; // correspond to siteContactSubject 1 : Je suis en panne
      case 2:
        return 2; // correspond to siteContactSubject 2 : Prendre rdv
      case 3:
        return 3; // correspond to siteContactSubject 3 : informations
      default:
        return 4; // correspond to siteContactSubject 4 : Autres
    }
  };

  const titleTag = headUtils.formatPageTitle(`${props.prestation.title} ${props.agency.name}`);
  const descriptionTag = headUtils.formatMetaDescription(props.prestation.argumentation);

  return (
    <div>
      <Head>
        <title>{titleTag}</title>
        <meta name="description" content={descriptionTag} />
        <meta property="og:title" content={titleTag} />
        <meta property="og:description" content={descriptionTag} />
      </Head>
      {/* header */}
      <div className="full-width d-flex f-row relative">
        {/* Menu */}
        <div className="absolute absolute-right-top-corner index-2">
          <Menu items={utils.formatCMSDatas('menu', props)} />
        </div>
        <HeaderWithSlashContainer
          backgroundColor={colors.grey800}
          rightColor={prestationUtils.getColorCode(props.prestation?.prestationColorId)}
          rightComponent={(
            <div className="full-size d-flex f-column justify-center align-center pt-6 ph-4">
              <div
                className="mv-3 d-flex justify-center"
                style={{
                  width: 200,
                }}
              >
                <span
                  className="h1 medium-weight white-text text-align-center"
                  style={{
                    lineHeight: '50px',
                  }}
                >
                  {props.prestation.price}
                </span>
              </div>
              <span
                className={`small-text medium-weight white-text
                ${isMobile ? 'mt-2' : 'mt-6'}
                text-align-center`}
                style={{
                  maxWidth: 300,
                }}
              >
                {strings.prestationPriceWarning}
              </span>
            </div>
          )}
        >
          {isMobile ? null : (
            <Link href="/" className="mt-3 mb-4">
              <a><Image alt="logo" src={logo} width={206} height={206} /></a>
            </Link>
          )}
          <div className={`${isMobile ? 'mv-2 ph-2' : 'mb-6 mt-5 pl-5 ml-4'}`}>
            {props.hasNavigate ? (
              <ButtonComponent onClick={() => router.back()} theme="dark-grey">
                <div className="d-flex justify-center align-center">
                  <Image
                    alt="back-arrow"
                    className="rotate-90"
                    src={arrow}
                    width={20}
                  />
                  <span className="normal-weight">{strings.back}</span>
                </div>
              </ButtonComponent>
            ) : null}
            <div
              className={`${isMobile ? 'ml-2' : ''} mv-4`}
              style={{ marginBottom: contactBlockHeight / 2 + 32 }}
            >
              <h1
                style={{
                  backgroundColor: prestationUtils.getColorCode(props.prestation.prestationColorId),
                }}
                className="medium-weight white-text mb-0 mt-3 mh-0 uppercase ph-1 w-fit-content text-align-center"
              >
                {`${props.prestation.title} par ${props.agency?.name}`}
              </h1>
              <h2 className="h1 normal-weight white-text mh-0 mv-3">
                {prestationUtils.replaceAdherentName(props.prestation.subtitle, props.agency?.name)}
              </h2>
              <div
                className="grey-400-text normal-weight wysiwyg-text ws-pre-wrap mt-5"
                dangerouslySetInnerHTML={{ __html: props.prestation.argumentation }}
              />
            </div>
          </div>
        </HeaderWithSlashContainer>
      </div>
      {/* Content */}
      {/* Background grey slash */}
      <div
        className="relative full-width white-background align-center index-2"
        style={{ height: contentBlockHeight + contactBlockHeight / 2 + 32 }}
      >
        <div className="absolute absolute-left-top-corner full-width">
          {isMobile ? null : (
            <SlashContainer width="41%" backgroundColor={colors.grey100} offset={20}>
              <div style={{ height: contentBlockHeight + contactBlockHeight / 2 + 32 }} />
            </SlashContainer>
          )}
        </div>
        {/* Grey slash content */}
        <div
          ref={contentBlockRef}
          className="absolute fgIndex full-width"
          style={{ marginTop: contactBlockHeight / 2 + 16 }}
        >
          <div className="ph-3">
            <div className="d-flex f-column align-center mv-4">
              <div
                className={`d-flex f-row justify-between ${isMobile ? 'f-wrap' : ''}`}
                style={{ width: isMobile ? '100%' : '80%' }}
              >
                <div
                  className="wysiwyg-text grey-600-text normal-weight mr-5"
                  dangerouslySetInnerHTML={{ __html: props.prestation.details }}
                />
                <div className={isMobile ? 'd-flex full-width justify-center align-center mt-5' : 'mh-3'}>
                  {props.prestation?.equipmentTypeId === 1 ? null : (
                    <BreakdownInfoCard
                      equipment={props.prestation?.equipmentTypeId}
                      prestationType={props.prestation?.prestationTypeId}
                      agencyName={props.agency?.name}
                      contactUs={() => {
                        setDefaultModalSubject(getDefaultSubject(props.prestation?.prestationTypeId));
                        toggleContactModal(true);
                      }}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* contact info block */}
        <div
          ref={contactBlockRef}
          className="absolute fgIndex full-width d-flex justify-center align-center"
          style={{
            top: `-${contactBlockHeight / 2}px`,
            left: 0,
          }}
        >
          {
            props.hasNavigate
              ? <AgencyContactBlock agency={props.agency} beCalledBack={() => toggleContactModal(true)} />
              : (
                <div
                  className="white-background fgIndex pb-4 d-flex f-row justify-center align-center"
                  style={{
                    width: isMobile ? '100%' : '80%',
                  }}
                >
                  <SearchEngine
                    theme="full"
                    color={colors.grey800}
                    buttonTheme="primary"
                    inputTheme="dark"
                    withSearchIcon
                    initValues={{
                      need: router.query?.need,
                      equipment: router.query?.equipment,
                      zipcode: router.query?.zipcode,
                    }}
                    handleSubmit={(data) => searchEngineUtils.goToSearchPage(router, data)}
                  />
                </div>
              )
          }
        </div>
      </div>
      {/* good reasons block */}
      {props.prestation.goodReasonsBlock ? (
        <GoodReasonsBlock
          illustrationPath={props.prestation.goodReasonsBlock?.illustrationFilePath}
          color={prestationUtils.getColorCode(props.prestation.prestationColorId)}
          intro={props.prestation?.goodReasonsBlock?.intro}
          reasons={props.prestation?.goodReasonsBlock?.goodReasons}
        />
      ) : null}
      {/* other prestations block */}
      {
        props.otherPrestations?.length
          ? (
            <div className="grey-800-background p-6">
              <h2 className="h2 white-text medium-weight">{`${strings.ourOtherContracts} ${props?.agency?.name}`}</h2>
              <div className="d-flex f-row f-wrap justify-center">
                {
                  props.otherPrestations.map(({
                    title,
                    subtitle,
                    argumentation,
                    price,
                    prestationColorId,
                    id: prestationId,
                    agencyNetworkPrestation,
                    prestationTypeId,
                  }) => (
                    <div
                      key={prestationId}
                      className="d-flex m-3"
                      style={{ boxSizing: 'border-box' }}
                    >
                      <PrestationCard
                        prestationType={prestationTypeId}
                        subtitle={prestationUtils.replaceAdherentName(subtitle, props.agency?.name)}
                        width={getCardWidth()}
                        argumentation={argumentation}
                        price={price || agencyNetworkPrestation?.price}
                        color={prestationColorId}
                        subscribe={() => {
                          setDefaultModalSubject(getDefaultSubject(prestationTypeId));
                          toggleContactModal(true);
                        }}
                        className="full-height"
                        link={urlUtils.getPrestationUrl(
                          { id: prestationId, agencyId: props.agency.id, title },
                        )}
                        title={title}
                        hasBorders={false}
                      />
                    </div>
                  ))
                }
              </div>
            </div>
          )
          : null
      }
      {
        isContactModalOpen
          ? (
            <ContactModal
              closeModal={() => toggleContactModal(false, null)}
              source="PAGE PRESTATION"
              defaultSubject={defaultModalSubject}
              logo={props.agency?.logoFilePath}
              agencyId={props.agency?.id}
              openingHours={props.agency?.openingHours}
              agencyPhone={props.agency?.phones?.[0]?.phone}
            />
          )
          : null
      }
      {/* Reassurance banner */}
      <ReassuranceBanner items={utils.formatCMSReassurance(props)} />
      {/* Footer */}
      <Footer
        networkBtn={props.footerRseauLienBouton}
        clientItems={utils.formatCMSDatas('footerClient', props)}
        networkItems={utils.formatCMSDatas('footerRseau', props)}
        facebook={props.footerFacebook}
        instagram={props.footerInstagram}
        twitter={props.footerTwitter}
        mentions={props.mentions}
        legalItems={utils.formatCMSDatas('sousFooter', props)}
        isMobile={isMobile}
      />
    </div>
  );
};

export default PrestationDetails;

export async function getServerSideProps({ params, req }) {
  let menuAndFooter;
  let prestation;
  let otherPrestations;
  let agency;

  try {
    menuAndFooter = await menuAndFooterService.menuAndFooter();
  } catch (err) {
    menuAndFooter = {};
    console.error(err);
  }
  try {
    agency = await AgencyService.getOneAgency(params?.agencyId);
  } catch (err) {
    agency = {};
    console.error(err);
  }
  try {
    const prestations = await PrestationService.getPrestations(params?.agencyId);
    prestation = await PrestationService.getPrestation(params.id?.join('/')?.split('-')[0]);
    prestation = prestation?.data;

    const equipmentType = prestations?.data?.equipmentTypes?.find(
      ({ id }) => id === prestation?.equipmentTypeId,
    );

    otherPrestations = [...equipmentType?.networkPrestations, ...equipmentType?.agencyPrestations]
      .filter(({
        id, title, price, agencyNetworkPrestation,
      }) => id !== prestation?.id
          && title !== prestation?.title
          && (price || agencyNetworkPrestation?.price));

    if (!prestation?.price) {
      const price = [...equipmentType?.networkPrestations, ...equipmentType?.agencyPrestations]
        .find(({ id, title }) => id === prestation?.id && title === prestation?.title)
        ?.agencyNetworkPrestation?.price
          || [...equipmentType?.networkPrestations, ...equipmentType?.agencyPrestations]
            .find(({ id, title }) => id === prestation?.id && title === prestation?.title)
            ?.price;
      prestation = { ...prestation, price: price || null };
    }
  } catch (err) {
    prestation = null;
    otherPrestations = [];
    console.error(err);
  }
  if (!prestation) {
    return {
      redirect: {
        destination: '/404',
        permanent: false,
      },
    };
  }
  return {
    props: {
      ...menuAndFooter?.fields,
      prestation,
      agency: agency?.data,
      otherPrestations,
      hasNavigate: !!req.headers.referer?.includes(process.env.NEXT_PUBLIC_DOMAIN),
    },
  };
}
